import { css, html, PropertyValues, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { waitFor } from '../modules/async';
import { hashString } from '../modules/common';
import { KryptonElement } from './krypton-element';
import { ICONS } from '../modules/resources';
import { NotificationType, NotificationConstants } from '../modules/notification';
import { KryptonEventDetail } from './krypton-event-detail';

class NotificationConfig {
    blocking: boolean;
    autoDismissTimeout: number;

    constructor(blocking: boolean, autoDismissTimeout: number) {
        this.blocking = blocking;
        this.autoDismissTimeout = autoDismissTimeout;
    }
}

const CONFIG = new Map<NotificationType, NotificationConfig>();
CONFIG.set("unknown", new NotificationConfig(false, 0));
CONFIG.set("success", new NotificationConfig(false, 3000));
CONFIG.set("warning", new NotificationConfig(true, 0));
CONFIG.set("error", new NotificationConfig(true, 0));
CONFIG.set("info", new NotificationConfig(false, 3000));


export class NotificationElement extends KryptonElement {

    static styles = css`
        :host {
            color: var(--notification-text-color);
            background-color: #a6b1bd;
            min-height: 3em;
            width: var(--krypton-notification-width);
            max-width: var(--krypton-notification-max-width);
            display: -ms-grid;
            display: grid;
            -ms-grid-columns: 3em 1fr 3em;
            grid-template-columns: 3em 1fr 3em;
            grid-template-areas: "icon message control";
            pointer-events: none;
        }

        :host::before {
            grid-area: icon;
            -ms-grid-row: 1;
            -ms-grid-column: 1;
            grid-row: 1;
            grid-column: 1;
            content: '';
            background: url('${unsafeCSS(ICONS.unknown.file)}') #bfbfbf no-repeat center center;
            background-size: 1.75em 1.75em;
        }

        :host::after {
            grid-area: control;
            -ms-grid-row: 1;
            -ms-grid-column: 3;
            grid-row: 1;
            grid-column: 3;
            content: '';
            background: url('${unsafeCSS(ICONS.close.file)}') no-repeat center center;
            background-size: 1em 1em;
            display: flex;
            align-items: center;
            cursor: pointer;
            color: var(--notification-count-color);
            font-weight: bold;
            font-family: consolas;
            pointer-events: all;
        }

        :host(.overflow)::after {
            background: url('${unsafeCSS(ICONS.chevron_white.file)}') no-repeat center center;
            background-size: 1em 1em;
        }

        :host(.overflow.expand)::after {
            background: url('${unsafeCSS(ICONS.close.file)}') no-repeat center center;
            background-size: 1em 1em;
        }

        :host(.wrap) #content {
            overflow-wrap: break-word;
            word-wrap: break-word;
            -ms-word-break: break-all;
            word-break: break-all;
            word-break: break-word;
            -ms-hyphens: auto;
            -moz-hyphens: auto;
            -webkit-hyphens: auto;
            hyphens: auto;
        }

        #content {
            grid-area: message;
            -ms-grid-row: 1;
            -ms-grid-column: 2;
            grid-row: 1;
            grid-column: 2;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
            padding: 0.5em 1em;
            overflow: hidden;
        }

        li {
            list-style: none;
        }

        #count {
            width: 1.25em;
            height: 1.25em;
            border-radius: 50%;
            font-size: .9em;
            color: #fff;
            line-height: 1.25em;
            text-align: center;
            background: #bfbfbf;
            -webkit-box-shadow: 1px 1px 0 0 rgba(0, 0, 0, 0.1);
            -moz-box-shadow: 1px 1px 0 0 rgba(0, 0, 0, 0.1);
            box-shadow: 1px 1px 0 0 rgba(0, 0, 0, 0.1);
            position: absolute;
            bottom: 0.25em;
            right: 3em;
            display: none;
        }

        :host(.count) #count {
            display: inline-block;
        }

        :host(.success) {
            background-color: #509e2f;
        }

        :host(.success)::before {
            background: url('${unsafeCSS(ICONS.check.file)}') #59c474 no-repeat center center;
            background-size: 1.75em 1.75em;
        }

        :host(.success) #count {
            background-color: #59c474;
        }

        :host(.info) {
            background-color: #6ea1d5;
        }

        :host(.info)::before {
            background: url('${unsafeCSS(ICONS.info.file)}') #94bee3 no-repeat center center;
            background-size: 1.75em 1.75em;
        }

        :host(.info) #count {
            background-color: #94bee3;
        }

        :host(.warning) {
            background-color: #f2a900;
        }

        :host(.warning)::before {
            background: url('${unsafeCSS(ICONS.warning.file)}') #ffc75d no-repeat center center;
            background-size: 1.75em 1.75em;
        }

        :host(.warning) #count {
            background-color: #ffc75d;
        }

        :host(.error) {
            background-color: #df134c;
        }

        :host(.error)::before {
            background: url('${unsafeCSS(ICONS.error.file)}') #e74c3c no-repeat center center;
            background-size: 1.75em 1.75em;
        }

        :host(.error) #count {
            background-color: #e74c3c;
        }

    `;

    render() {
        return html`
            <div id="content">
                ${this.messages.map(i => html`<li>${this.getRows(i.split("<br>"))}</li>`)}
                <slot></slot>
            </div>
            <span id="count">${this.count}</span>
        `;
    }

    getRows(lineItems) {
        return html`${Object.keys(lineItems).map(item =>
            html`<div>${lineItems[item].replace("&nbsp;", String.fromCodePoint(0x202f))}</div>`)}`;
    }

    _type: NotificationType = "unknown";

    get type(): NotificationType {
        return this._type;
    }

    @property({ type: String, reflect: true })
    set type(type: NotificationType) {
        const oldValue = this._type;
        this._type = type;
        this.classList.add(type);
        this.requestUpdate("type", oldValue);
    }

    private _count: number = 1;

   get count() {
        return this._count;
    }

    @property({ type: Number, reflect: true })
    set count(count) {
        const oldValue = this._count;
        this._count = count;
        if (count > 1) {
            this.classList.add("count");
        }
        this.requestUpdate("count", oldValue);
    }

    get blocking() {
        return CONFIG.get(this.type)?.blocking;
    }

    get autoDismissTimeout() {
        return CONFIG.get(this.type)?.autoDismissTimeout;
    }

    @property({ type: Array }) accessor messages: string[] = [];

    constructor() {
        super();
        this.addEventListener("click", e => {
            if (this.hasOverflow()) {
                this.expand();
            } else {
                this.dismiss();
            }
        });
    }

    add(message: string) {
        this.messages.push(message);
        this.requestUpdate("messages");
        if (this.hasOverflow()) {
            this.classList.add("overflow");
            this.requestUpdate("messages");
        }
    }

    dismiss() {
        this.dismissed = true;
        this.fire(KryptonEventDetail.NOTIFICATION_ELEMENT_EVENT, new KryptonEventDetail(KryptonEventDetail.DISMISS, KryptonEventDetail.NOTIFICATION_ELEMENT_EVENT, { dismiss: true }));
    }

    async expand() {
        this.style.setProperty("width", this.overflowWidth + "px");
        this.classList.add("expand");
        await waitFor(NotificationConstants.animationTime);
        if (this.hasOverflow()) {
            this.classList.add("wrap");
        }
    }

    private overflowWidth: number;

    hasOverflow() {
        this.overflowWidth = this.shadowRoot?.getElementById("content")?.scrollWidth || 0;
        return this.overflowWidth > this.offsetWidth;
    }

    firstUpdated(changedPropertes: PropertyValues) {
        if (this.autoDismissTimeout) this.async(this.dismiss.bind(this), this.autoDismissTimeout, "auto-dismiss");
        this.async(() => this.hasOverflow() && this.classList.add("overflow"));
        super.firstUpdated(changedPropertes);
    }

    //TODO dismissAll? there was a function in the old element but it doesn't make sense to have it here

    hashCode(): number {
        let result = hashString(this.type);
        this.messages.forEach(m => result += hashString(m));
        return result;
    }

    dismissed: boolean = false;

}

declare global {
    interface HTMLElementTagNameMap {
        'notification-element': NotificationElement;
    }
}